// Cross-browser Fullscreen API wrapper. Pure browser helpers, no React.
//
// The standard surface is well-supported (Chrome/Edge/Firefox 64+), but Safari
// still ships the webkit-prefixed surface as of 2026. We probe both at runtime
// and call whichever exists.
//
// Spec: MVP 2.0 #8 — Auto-fullscreen at attempt start + exit detection.

// Safari + older Chromium still expose webkit-prefixed variants. The TS lib
// types don't include them, so we widen `Document` / `Element` locally.
interface FullscreenDocument extends Document {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
}

interface FullscreenElement extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void> | void;
}

function fsDoc(): FullscreenDocument {
  return document as FullscreenDocument;
}

function fsRoot(): FullscreenElement {
  return document.documentElement as FullscreenElement;
}

export function isFullscreenSupported(): boolean {
  if (typeof document === "undefined") return false;
  const root = fsRoot();
  return (
    typeof root.requestFullscreen === "function" ||
    typeof root.webkitRequestFullscreen === "function"
  );
}

export function isFullscreen(): boolean {
  if (typeof document === "undefined") return false;
  const d = fsDoc();
  return Boolean(d.fullscreenElement ?? d.webkitFullscreenElement);
}

export function requestFullscreen(): Promise<void> {
  const root = fsRoot();
  if (typeof root.requestFullscreen === "function") {
    // Some older Safari builds return undefined instead of a Promise.
    const r = root.requestFullscreen();
    return r instanceof Promise ? r : Promise.resolve();
  }
  if (typeof root.webkitRequestFullscreen === "function") {
    const r = root.webkitRequestFullscreen();
    return r instanceof Promise ? r : Promise.resolve();
  }
  return Promise.reject(new Error("Fullscreen API is not supported."));
}

export function exitFullscreen(): Promise<void> {
  const d = fsDoc();
  if (!isFullscreen()) return Promise.resolve();
  if (typeof d.exitFullscreen === "function") {
    const r = d.exitFullscreen();
    return r instanceof Promise ? r : Promise.resolve();
  }
  if (typeof d.webkitExitFullscreen === "function") {
    const r = d.webkitExitFullscreen();
    return r instanceof Promise ? r : Promise.resolve();
  }
  return Promise.resolve();
}

// Subscribes to fullscreenerror + webkitfullscreenerror and returns a cleanup
// thunk.
//
// This is the ONLY failure signal Safari gives us. `requestFullscreen()`
// resolves through the `r instanceof Promise ? r : Promise.resolve()` branch
// above whenever the prefixed call returns undefined — which on Safari is the
// NORMAL return — so the caller's success path runs even when the request was
// actually refused. Without this listener a Safari candidate whose request is
// denied sits behind a blocking overlay that never clears while the recruiter
// sees nothing explaining why.
export function onFullscreenError(cb: () => void): () => void {
  if (typeof document === "undefined") return () => {};
  document.addEventListener("fullscreenerror", cb);
  document.addEventListener("webkitfullscreenerror", cb);
  return () => {
    document.removeEventListener("fullscreenerror", cb);
    document.removeEventListener("webkitfullscreenerror", cb);
  };
}

// Subscribes to fullscreenchange + webkitfullscreenchange and returns a cleanup
// thunk. The callback is invoked on BOTH enter and exit — call `isFullscreen()`
// inside it to disambiguate.
export function onFullscreenChange(cb: () => void): () => void {
  if (typeof document === "undefined") return () => {};
  document.addEventListener("fullscreenchange", cb);
  document.addEventListener("webkitfullscreenchange", cb);
  return () => {
    document.removeEventListener("fullscreenchange", cb);
    document.removeEventListener("webkitfullscreenchange", cb);
  };
}
